import { useEffect, useLayoutEffect, useRef } from 'react';

const HANDLE = '.handles-layer__drag-handle, .handles-layer__connection-handle, .handles-layer__decision-group, .handles-layer__branch-btn';
const CARD = '.task-node, .decision-node, .milestone-node, .note-node';
// Long enough to cross the gap onto the button, short enough that leaving still feels immediate.
const HOLD_MS = 80;

function taskIdFrom(target) {
  const card = target.closest?.(CARD);
  if (card?.dataset?.taskId) return card.dataset.taskId;
  const handle = target.closest?.(HANDLE);
  if (!handle) return null;
  return handle.dataset?.taskId || handle.closest?.('[data-task-id]')?.dataset?.taskId || null;
}

function paintHandles(layer, hotId, pinnedIds) {
  if (!layer) return;
  const marks = layer.querySelectorAll('[data-task-id]');
  marks.forEach((el) => {
    if (el.closest('.handles-layer__edge-buttons')) return;
    const id = el.dataset.taskId;
    el.classList.toggle('handles-layer__hot', !!hotId && id === hotId);
    el.classList.toggle('handles-layer__pinned', !!pinnedIds?.has(id));
  });
}

/** Flip only the card being left and the card being entered. */
function applyHot(layer, taskId, hotNodes) {
  const prev = hotNodes.current;
  for (let i = 0; i < prev.length; i += 1) prev[i].classList.remove('handles-layer__hot');
  const next = [];
  hotNodes.current = next;
  if (!layer || !taskId) return;
  const escaped = window.CSS?.escape ? window.CSS.escape(taskId) : String(taskId);
  layer.querySelectorAll(`[data-task-id="${escaped}"]`).forEach((el) => {
    if (el.closest('.handles-layer__edge-buttons')) return;
    el.classList.add('handles-layer__hot');
    next.push(el);
  });
}

/**
 * Show a card's buttons on the pointer move itself. No React render in between,
 * so it lands in the same paint as a CSS hover.
 */
export function useInstantHandleHover({ enabled, containerRef, layerRef, pinnedIds }) {
  const hotRef = useRef(null);
  const hotNodes = useRef([]);
  const pinnedRef = useRef(pinnedIds);
  pinnedRef.current = pinnedIds;

  useLayoutEffect(() => {
    if (!enabled) return;
    paintHandles(layerRef.current, hotRef.current, pinnedRef.current);
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const container = containerRef?.current;
    if (!container) return undefined;

    let hold = null;
    const show = (taskId) => {
      if (hold) {
        clearTimeout(hold);
        hold = null;
      }
      if (hotRef.current === taskId) return;
      hotRef.current = taskId;
      applyHot(layerRef.current, taskId, hotNodes);
    };
    const hideSoon = () => {
      if (hold) return;
      hold = setTimeout(() => {
        hold = null;
        hotRef.current = null;
        applyHot(layerRef.current, null, hotNodes);
      }, HOLD_MS);
    };

    const onMove = (event) => {
      const taskId = taskIdFrom(event.target);
      if (taskId) show(taskId);
      else if (hotRef.current) hideSoon();
    };
    const onLeave = () => {
      if (hold) {
        clearTimeout(hold);
        hold = null;
      }
      hotRef.current = null;
      applyHot(layerRef.current, null, hotNodes);
    };

    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseleave', onLeave);
    return () => {
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseleave', onLeave);
      if (hold) clearTimeout(hold);
    };
  }, [enabled, containerRef, layerRef]);
}
